import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Box
} from "@material-ui/core";
import { Search as SearchIcon, Check as CheckIcon, Warning as WarningIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      minWidth: 400,
      maxWidth: 600,
      maxHeight: 600
    }
  },
  searchField: {
    marginBottom: theme.spacing(2)
  },
  listItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  selectedItem: {
    backgroundColor: theme.palette.primary.light,
    "&:hover": {
      backgroundColor: theme.palette.primary.main
    }
  },
  checkIcon: {
    color: theme.palette.primary.main
  },
  infoBanner: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1)
  },
  infoBannerWithAI: {
    backgroundColor: theme.palette.mode === 'dark' ? '#78350f' : '#fef3c7',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#f59e0b' : '#fbbf24'}`,
    color: theme.palette.mode === 'dark' ? '#fbbf24' : '#92400e'
  },
  infoBannerWithoutAI: {
    backgroundColor: theme.palette.mode === 'dark' ? '#78350f' : '#fef3c7',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#f59e0b' : '#fbbf24'}`,
    color: theme.palette.mode === 'dark' ? '#fbbf24' : '#92400e'
  }
}));

const TransferQueueModal = ({ 
  open, 
  onClose, 
  onSelect, 
  currentTransferQueueId = null,
  excludedQueueIds = [], // ✅ NUEVO: IDs de departamentos ya usados en otras opciones del nivel 1
  currentOptionId = null // ✅ NUEVO: ID de la opción actual (para permitir mantener su departamento)
}) => {
  const classes = useStyles();
  const [queues, setQueues] = useState([]);
  const [filteredQueues, setFilteredQueues] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedQueueId, setSelectedQueueId] = useState(currentTransferQueueId);
  const [queueAIInfo, setQueueAIInfo] = useState(null); // ✅ NUEVO: Información de automatización del departamento seleccionado
  const [loadingAIInfo, setLoadingAIInfo] = useState(false); // ✅ NUEVO: Estado de carga de información de IA

  // ✅ Cargar TODOS los departamentos (no solo con IA)
  useEffect(() => {
    const fetchQueues = async () => {
      setLoading(true);
      try {
        // ✅ CAMBIO: Usar endpoint normal que retorna TODOS los departamentos
        const { data } = await api.get("/queue");
        
        // ✅ Filtrar departamentos:
        // 1. Excluir el departamento actual (si no es la opción que estamos editando)
        // 2. Excluir departamentos ya usados en otras opciones del nivel 1
        const availableQueues = data.filter(queue => {
          // Si es el departamento actual de esta opción, permitirlo (para poder mantenerlo)
          if (queue.id === currentTransferQueueId) {
            return true;
          }
          // Excluir departamentos ya usados en otras opciones
          return !excludedQueueIds.includes(queue.id);
        });
        
        setQueues(availableQueues);
        setFilteredQueues(availableQueues);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchQueues();
      // ✅ CRÍTICO: NO resetear selectedQueueId si ya tiene valor
      // Esto permite que el useEffect de abajo cargue la info automáticamente
      if (!currentTransferQueueId) {
        setQueueAIInfo(null);
        setSelectedQueueId(null);
      } else {
        // ✅ Si hay transferQueueId, mantenerlo para que se cargue su info
        setSelectedQueueId(currentTransferQueueId);
      }
    }
  }, [open, currentTransferQueueId, excludedQueueIds]);

  // ✅ Filtrar departamentos por búsqueda
  useEffect(() => {
    const filtered = queues.filter(queue =>
      queue.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredQueues(filtered);
  }, [searchTerm, queues]);

  // ✅ NUEVO: Obtener información de automatización del departamento seleccionado
  useEffect(() => {
    const fetchQueueAIInfo = async () => {
      if (!selectedQueueId) {
        setQueueAIInfo(null);
        return;
      }

      setLoadingAIInfo(true);
      try {
        const { data } = await api.get(`/queue/${selectedQueueId}/check-ai`);
        if (data.success) {
          setQueueAIInfo(data.data);
        } else {
          // ✅ Si no hay éxito, asumir que no tiene automatización
          setQueueAIInfo({ hasAI: false, automationType: null, automationName: null });
        }
      } catch (error) {
        console.error("Error obteniendo información de IA:", error);
        // ✅ En caso de error, asumir que no tiene automatización
        setQueueAIInfo({ hasAI: false, automationType: null, automationName: null });
      } finally {
        setLoadingAIInfo(false);
      }
    };

    if (selectedQueueId) {
      fetchQueueAIInfo();
    } else {
      setQueueAIInfo(null);
    }
  }, [selectedQueueId]);

  const handleQueueSelect = (queue) => {
    setSelectedQueueId(queue.id);
  };

  const handleConfirm = () => {
    if (selectedQueueId) {
      const selectedQueue = queues.find(q => q.id === selectedQueueId);
      onSelect({
        id: selectedQueue.id,
        name: selectedQueue.name
      });
      onClose();
    } else {
      toast.error("Por favor selecciona un departamento");
    }
  };

  const handleCancel = () => {
    setSelectedQueueId(null);
    setSearchTerm("");
    setQueueAIInfo(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} className={classes.root}>
      <DialogTitle>
        {i18n.t("transferQueueModal.title")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {i18n.t("transferQueueModal.description")}
        </Typography>
        
        <TextField
          fullWidth
          variant="outlined"
          placeholder={i18n.t("transferQueueModal.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
            <CircularProgress />
          </div>
        ) : (
          <List>
            {filteredQueues.map((queue) => (
              <ListItem
                key={queue.id}
                className={`${classes.listItem} ${
                  selectedQueueId === queue.id ? classes.selectedItem : ""
                }`}
                onClick={() => handleQueueSelect(queue)}
              >
                <ListItemText
                  primary={queue.name}
                  secondary={`ID: ${queue.id}`}
                />
                <ListItemSecondaryAction>
                  {selectedQueueId === queue.id && (
                    <IconButton edge="end" className={classes.checkIcon}>
                      <CheckIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {!loading && filteredQueues.length === 0 && (
          <Typography variant="body2" color="textSecondary" align="center">
            {searchTerm 
              ? i18n.t("transferQueueModal.noResults") 
              : i18n.t("transferQueueModal.noQueues")
            }
          </Typography>
        )}

        {/* ✅ NUEVO: Banner informativo después de seleccionar departamento */}
        {selectedQueueId && !loadingAIInfo && queueAIInfo && (
          <Box 
            className={`${classes.infoBanner} ${
              queueAIInfo?.hasAI 
                ? classes.infoBannerWithAI 
                : classes.infoBannerWithoutAI
            }`}
          >
            <WarningIcon style={{ marginTop: '2px', flexShrink: 0 }} />
            <Box style={{ flex: 1 }}>
              {queueAIInfo?.hasAI ? (
                <>
                  <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    <span role="img" aria-label="advertencia">⚠️</span> Este departamento tiene activado AGENTE IA
                  </Typography>
                  <Typography variant="body2" style={{ marginBottom: '2px' }}>
                    Tipo: {queueAIInfo.automationType === 'prompt' ? 'Prompt' : 
                           queueAIInfo.automationType === 'chatbot' ? 'Chatbot' : 
                           queueAIInfo.automationType === 'integration' ? 'Integración' : 'Desconocido'}
                  </Typography>
                  {queueAIInfo.automationName && (
                    <Typography variant="body2" style={{ marginBottom: '2px' }}>
                      Nombre: {queueAIInfo.automationName}
                    </Typography>
                  )}
                  <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: '4px' }}>
                    Se transferirá a pestaña: AGENTES IA
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    <span role="img" aria-label="advertencia">⚠️</span> Este departamento no tiene automatización
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: '4px' }}>
                    Se transferirá a pestaña: PENDIENTES
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        )}

        {loadingAIInfo && selectedQueueId && (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="secondary">
          {i18n.t("transferQueueModal.cancel")}
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="primary" 
          variant="contained"
          disabled={!selectedQueueId}
        >
          {i18n.t("transferQueueModal.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferQueueModal; 